import * as React from 'react'
import cx from 'classnames';
import { I18n } from "react-redux-i18n";
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';

import { FormControl } from '@common/components/form-control';
import { Radio } from '@common/components/radio';
import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import TreeDropdown from "@common/components/TreeDropdown";
import { getItem } from "@common/components/Tree";
import { Button } from '@common/components/Button';
import { FolderListDto } from "@common/dto/folder/list-dto";
import { HttpErrorDto } from "@common/dto/http";
import { MODULE_NAME } from "@modules/individuals";
import { checkIndividualNameExists, createIndividual } from "@modules/individuals/actions/individuals";
import { IndividualGender } from '@common/dto/individual/gender';

import { IndividualCreateDto } from "../../dto/create-individual";
import { NewQuestionnaire } from '@common/components/new-questionnaire';
import { getFolders } from '@common/actions/folders';
import { getQuestionnaires, launchNewIndividualQuestionnaire } from '@common/actions/questionnaires';
import { InviteListDto } from '@common/dto/invite/list-dto';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';

import './new-individual.styles.scss';

type NewIndividualProps = {
  onClose: () => void;
  onSubmit: (globalId: InviteListDto['id']) => void;
  currentFolderId: FolderListDto['id'];
  errors?: string[];
  showErrorMessage: (message?: string) => void;
};

export const NewIndividual = ({
  currentFolderId,
  onClose,
  onSubmit,
  showErrorMessage
}: NewIndividualProps) => {


  const [errors, setErrors] = React.useState<{ individualName: string[]; homeFolderId: string[] }>({ individualName: [], homeFolderId: [] });
  const [submitting, setSubmitting] = React.useState(false);
  const [folders, setFolders] = React.useState<FolderListDto[]>([]);
  const [individualIdForNewQuestionnaire, setIndividualIdForNewQuestionnaire] = React.useState<InviteListDto['id']>();

  React.useEffect(() => {
    getFolders({ context: 1, addPedCheck: true })
      .then(setFolders)
      .catch((err) => {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity ?? I18n.t('Failed to get folders'));
      });
  }, [showErrorMessage]);

  const [state, setState] = React.useState<IndividualCreateDto>({
    name: '',
    gender: IndividualGender.male,
    homeFolderId: currentFolderId
  });

  const handleFormFields = React.useCallback((newFieldValue: Partial<IndividualCreateDto>) => {
    setState((prevState) => ({
      ...prevState,
      ...newFieldValue
    }));
  }, [setState]);

  const handleGenderField = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFormFields({
      gender: parseInt(e.target.value),
    });
  }, [handleFormFields]);

  const handleNewIndividualName = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFormFields({
      name: e.target.value,
    });
    setErrors((prev) => ({
      ...prev,
      individualName: []
    }));
  }, [handleFormFields, setErrors]);

  const handleFolderId = React.useCallback((homeFolderId: number) => {
    handleFormFields({
      homeFolderId
    });
    setErrors((prev) => ({
      ...prev,
      homeFolderId: []
    }));
  }, [handleFormFields, setErrors]);

  const getFolderValidationError = React.useCallback((): string | null => {
    if (!folders.length) {
      return I18n.t('You do not have access to any folders');
    }
    if (!getItem(state.homeFolderId, folders)?.id) {
      return I18n.t('Please select a folder');
    }
    return null;
  }, [folders, state.homeFolderId]);

  const handleCloseQuestionnaireOpen = React.useCallback(() => {
    setIndividualIdForNewQuestionnaire(undefined);
  }, [setIndividualIdForNewQuestionnaire]);

  const createNewIndividual = React.useCallback(async (newIndividualForm: IndividualCreateDto, onSubmit: (globalId: InviteListDto['id']) => void) => {
    try {
      const folderError = getFolderValidationError();
      if (folderError) {
        setErrors((prev) => ({
          ...prev,
          homeFolderId: [folderError]
        }));
        return;
      }
      setErrors((prev) => ({
        ...prev,
        homeFolderId: []
      }));

      if (!state.name) {
        setErrors((prev) => ({
          ...prev,
          individualName: [
            I18n.t("Specify name of new individual")
          ]
        }));
        return;
      }

      setSubmitting(true);

      if (await checkIndividualNameExists(newIndividualForm)) {
        showErrorMessage(I18n.t("Individual name already exists in this folder"));
        return;
      }

      const createdIndividual = await createIndividual(newIndividualForm);
      if (createdIndividual.errors?.length) {
        showErrorMessage(createdIndividual.errors[0]);
        return;
      }

      onSubmit(createdIndividual.data);
    } catch (err) {
      showErrorMessage((err as HttpErrorDto)?.entity);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, showErrorMessage, onClose, state.name, getFolderValidationError]);

  const handleQuestionnaireOpen = React.useCallback(async () => {
    createNewIndividual(state, setIndividualIdForNewQuestionnaire);
  }, [setIndividualIdForNewQuestionnaire, state, createNewIndividual]);

  const handleSubmit = React.useCallback(() => {
    createNewIndividual(state, onSubmit);
  }, [onSubmit, state, createNewIndividual]);

  const handleNewQuestionnaireSubmit = React.useCallback(async (questionnaire: QuestionnaireListDto | undefined) => {
    if (!individualIdForNewQuestionnaire) {
      return;
    }

    const redirectUrl = await launchNewIndividualQuestionnaire(
      individualIdForNewQuestionnaire, 
      questionnaire?.questionnaireId,
      questionnaire?.fhq4
    );
    window.location.href = redirectUrl;
  }, [individualIdForNewQuestionnaire]);

  const handleGetQuestionnaires = async () => {
    const questionnaires = await getQuestionnaires({
      includeFamilyQuestionnaires: false,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });

    return questionnaires;
  }

  return (
    <Modal
      show
      bsSize="lg"
      bsClass="NewIndividual bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes(MODULE_NAME, 'new-individual-modal-title', 'header')}
        >
          {I18n.t("Add New Individual")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className="NewIndividual__body">
        {
          individualIdForNewQuestionnaire ? (
            <NewQuestionnaire
              moduleName={MODULE_NAME}
              onClose={handleCloseQuestionnaireOpen}
              title={`${I18n.t("New Questionnaire for")} ${state.name}`}
              showErrorMessage={showErrorMessage}
              hasEmail={true}
              getQuestionnaires={handleGetQuestionnaires}
              onSubmit={handleNewQuestionnaireSubmit}
            />
          ) : (
            <Flex justify="center" alignItems="center" className="NewIndividual__content">
              <div className="NewIndividual__content__margined-section">
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Folder Name')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                    <TreeDropdown
                      className={cx('NewIndividual__content__folders', { 'NewIndividual__content__input-error': errors.homeFolderId?.length > 0 })}
                      items={folders}
                      value={state.homeFolderId}
                      onChange={handleFolderId}
                      {...getTestAttributes(MODULE_NAME, 'home-folder-id', 'select')}
                    />
                    {errors.homeFolderId?.map((error) => (
                      <div className="NewIndividual__content__errors" key={error}>
                        {error}
                      </div>
                    ))}
                  </div>
                </Flex>
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Individual Name')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                    <FormControl
                      type="text"
                      defaultValue={state.name}
                      onChange={handleNewIndividualName}
                      className={errors.individualName?.length > 0 ? 'NewIndividual__content__input-error' : ''}
                      {...getTestAttributes({ id: "tx-ind-name" })}
                    />
                    {errors.individualName?.map((error) => (
                      <div className="NewIndividual__content__errors" key={error}>
                        {error}
                      </div>
                    ))}
                  </div>
                </Flex>
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Sex')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                      <Radio
                        className="NewIndividual__content__gender-field"
                        name="gender"
                        value={IndividualGender.male}
                        defaultChecked
                        onChange={handleGenderField}
                        {...getTestAttributes({ id: "radio-ind-male-input" })}
                      >
                        {I18n.t('Male')}
                      </Radio>
                      <Radio
                        className="NewIndividual__content__gender-field"
                        name="gender"
                        value={IndividualGender.female}
                        onChange={handleGenderField}
                        {...getTestAttributes({ id: "radio-ind-female-input" })}
                      >
                        {I18n.t('Female')}
                      </Radio>
                      <Radio
                        className="NewIndividual__content__gender-field"
                        name="gender"
                        value={IndividualGender.unknown}
                        onChange={handleGenderField}
                        {...getTestAttributes({ id: "radio-ind-unknown-input" })}
                      >
                        {I18n.t('Unknown')}
                      </Radio>
                  </div>
                </Flex>
              </div>
            </Flex>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between">
          <Button
            onClick={onClose}
            {...getTestAttributes(MODULE_NAME, 'new-individual-modal-close', 'button')}
          >
            {I18n.t('Cancel')}
          </Button>
          <Flex justify="space-between">
            <Button
              onClick={handleQuestionnaireOpen}
              disabled={submitting || Boolean(errors.individualName?.length)}
              {...getTestAttributes(MODULE_NAME, 'new-individual-launch-new-questionnaire', 'button')}
            >
              {I18n.t('Launch New Questionnaire')}
            </Button>
            <Button
              bsStyle="primary"
              onClick={handleSubmit}
              disabled={submitting || Boolean(errors.individualName?.length)}
              {...getTestAttributes({ id: "btn-ind-save" })}
            >
              {I18n.t('Create')}
            </Button>
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  )
};